<template>
  <div class="flex flex-col w-full">
    <!-- Hoppscotch Cloud Entry (always first, with lock icon) -->
    <button
      class="org-item"
      :class="{ active: isOnDefaultInstance }"
      @click="handleSwitchToHoppscotch"
    >
      <div
        class="org-avatar bg-primaryLight text-secondaryDark overflow-hidden"
      >
        <img
          src="/logo.svg"
          alt="Hoppscotch Cloud"
          class="h-full w-full object-cover"
        />
      </div>
      <div class="org-info">
        <span class="org-name">{{
          t("organization_sidebar.hoppscotch_cloud")
        }}</span>
        <span class="org-domain">{{ rootDomain }}</span>
      </div>
      <div class="ml-auto flex items-center gap-2">
        <IconLucideCheck
          class="h-4 w-4"
          :class="isOnDefaultInstance ? 'text-accent' : 'invisible'"
        />
        <IconLucideLock class="h-3.5 w-3.5 text-secondary" />
      </div>
    </button>

    <!-- Organizations Section (only visible when signed in) -->
    <template v-if="isSignedIn">
      <!-- Section Header -->
      <div
        v-if="userEmail"
        class="flex items-center justify-between border-b border-dividerLight px-4 py-2"
      >
        <span class="text-xs text-secondary truncate">
          {{
            t("organization_sidebar.organizations_for", { email: userEmail })
          }}
        </span>
        <span
          v-tippy="{
            theme: 'tooltip',
            content: t('organization_sidebar.multi_account_notice'),
            placement: 'top',
            maxWidth: 280,
            appendTo: appendToBody,
            onCreate: onTooltipCreate,
          }"
          class="flex-shrink-0 ml-2"
        >
          <IconLucideInfo class="h-3.5 w-3.5 text-secondary cursor-help" />
        </span>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex flex-col">
        <div
          v-for="ii in 3"
          :key="ii"
          class="flex items-center px-4 py-2.5 border-b border-dividerLight"
        >
          <div
            class="h-8 w-8 rounded-md bg-primaryLight animate-pulse shrink-0"
          />
          <div class="flex flex-col ml-3 flex-1 gap-1.5">
            <div class="h-3.5 w-24 rounded bg-primaryLight animate-pulse" />
            <div class="h-3 w-40 rounded bg-primaryLight animate-pulse" />
          </div>
          <div
            class="h-4 w-12 rounded bg-primaryLight animate-pulse ml-auto shrink-0"
          />
        </div>
      </div>

      <!-- Organizations List -->
      <template v-else-if="organizationItems.length > 0">
        <!-- Empty active orgs state (only inactive orgs exist) -->
        <div v-if="activeOrganizations.length === 0" class="px-4 py-3">
          <p class="text-xs text-secondary">
            {{ t("organization_sidebar.no_active_orgs_found") }}
          </p>
        </div>

        <!-- Active Organizations -->
        <button
          v-for="item in activeOrganizations"
          :key="item.organization.orgID"
          class="org-item"
          :class="{ active: item.isActive }"
          @click="handleSwitchToOrganization(item.organization.orgDomain)"
        >
          <div
            v-if="item.organization.orgLogo"
            class="org-avatar overflow-hidden"
          >
            <img
              :src="sanitizeLogoUrl(item.organization.orgLogo)"
              :alt="item.organization.orgName"
              class="h-full w-full object-cover"
            />
          </div>
          <div
            v-else
            class="org-avatar"
            :class="getOrgColor(item.organization.orgName)"
          >
            {{ getOrgInitials(item.organization.orgName) }}
          </div>
          <div class="org-info">
            <span class="org-name">
              {{ item.organization.orgName }}
            </span>
            <span class="org-domain">
              {{ item.organization.orgDomain }}.{{ rootDomain }}
            </span>
          </div>
          <div class="ml-auto flex items-center gap-2 shrink-0">
            <a
              v-if="item.isAdmin"
              class="org-badge org-badge-link"
              :href="getAdminUrl(item.organization.orgDomain)"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >
              {{ t("organization_sidebar.admin") }}
              <IconLucideArrowUpRight class="h-3 w-3" />
            </a>
            <IconLucideCheck
              class="h-4 w-4"
              :class="item.isActive ? 'text-accent' : 'invisible'"
            />
          </div>
        </button>

        <!-- Inactive Organizations Section -->
        <template v-if="inactiveOrganizations.length > 0">
          <div
            class="flex items-center border-b border-dividerLight justify-between px-4 py-2"
          >
            <span
              class="text-xs font-semibold text-secondary tracking-wide whitespace-nowrap"
            >
              {{ t("organization_sidebar.inactive_orgs") }}
            </span>
            <span
              v-tippy="{
                theme: 'tooltip',
                content: t('organization_sidebar.inactive_orgs_tooltip'),
                placement: 'top',
                maxWidth: 280,
                appendTo: appendToBody,
                onCreate: onTooltipCreate,
              }"
              class="flex-shrink-0"
            >
              <IconLucideInfo class="h-3.5 w-3.5 text-secondary cursor-help" />
            </span>
          </div>

          <button
            v-for="item in inactiveOrganizations"
            :key="item.organization.orgID"
            class="org-item org-item-inactive"
            :class="{ active: item.isActive }"
            @click="handleSwitchToOrganization(item.organization.orgDomain)"
          >
            <div
              v-if="item.organization.orgLogo"
              class="org-avatar overflow-hidden"
            >
              <img
                :src="sanitizeLogoUrl(item.organization.orgLogo)"
                :alt="item.organization.orgName"
                class="h-full w-full object-cover opacity-60"
              />
            </div>
            <div
              v-else
              class="org-avatar"
              :class="getOrgColor(item.organization.orgName)"
            >
              {{ getOrgInitials(item.organization.orgName) }}
            </div>
            <div class="org-info">
              <span class="org-name">
                {{ item.organization.orgName }}
              </span>
              <span class="org-domain">
                {{ item.organization.orgDomain }}.{{ rootDomain }}
              </span>
            </div>
            <div class="ml-auto flex items-center gap-2 shrink-0">
              <a
                v-if="item.isAdmin"
                class="org-badge org-badge-link"
                :href="getAdminUrl(item.organization.orgDomain)"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >
                {{ t("organization_sidebar.admin") }}
                <IconLucideArrowUpRight class="h-3 w-3" />
              </a>
              <IconLucideCheck
                class="h-4 w-4"
                :class="item.isActive ? 'text-accent' : 'invisible'"
              />
            </div>
          </button>
        </template>
      </template>

      <!-- Error State -->
      <div v-else-if="error" class="p-4 text-center space-y-3">
        <IconLucideAlertCircle class="mx-auto h-6 w-6 text-red-500" />
        <p class="text-xs text-red-500">
          {{ t("organization_sidebar.error_loading") }}
        </p>
        <HoppButtonSecondary
          :label="t('action.retry')"
          :icon="IconLucideRefreshCw"
          outline
          @click="retryFetch"
        />
      </div>

      <!-- Empty State (no organizations) -->
      <div v-else class="px-4 py-3">
        <p class="text-xs text-secondary">
          {{ t("organization_sidebar.no_orgs_found") }}
        </p>
      </div>
    </template>

    <!-- Create Organization CTA (always visible, even when signed out) -->
    <button class="org-item org-item-cta" @click="handleInitiateOnboarding">
      <IconLucidePlus class="h-4 w-4 text-secondary shrink-0" />
      <span class="ml-3 text-sm text-secondary">
        {{ t("organization.create_an_organization") }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { OrganizationStatus } from "@app/helpers/backend/graphql"
import { OrganizationSwitcherService } from "@app/services/organization-switcher.service"
import { useI18n } from "@composables/i18n"
import { useReadonlyStream } from "@hoppscotch/common/composables/stream"
import {
  getOrgColor,
  getOrgInitials,
  sanitizeLogoUrl,
} from "@hoppscotch/common/helpers/utils/organization"
import { invokeAction } from "@hoppscotch/common/helpers/actions"
import { platform } from "@hoppscotch/common/platform"
import { useService } from "dioc/vue"
import { computed } from "vue"
import IconLucideAlertCircle from "~icons/lucide/alert-circle"
import IconLucideArrowUpRight from "~icons/lucide/arrow-up-right"
import IconLucideCheck from "~icons/lucide/check"
import IconLucideInfo from "~icons/lucide/info"
import IconLucideLock from "~icons/lucide/lock"
import IconLucidePlus from "~icons/lucide/plus"
import IconLucideRefreshCw from "~icons/lucide/refresh-cw"

const emit = defineEmits<{
  (e: "close-dropdown"): void
}>()

// Append nested tooltips to body so they aren't clipped by the popover
const appendToBody = () => document.body

// The tooltip theme applies `truncate` (white-space: nowrap) on .tippy-content,
// which clips multi-line tooltip text. This callback resets it to allow wrapping.
const onTooltipCreate = (instance: any) => {
  const content = instance.popper?.querySelector(".tippy-content")
  if (content) {
    content.style.whiteSpace = "normal"
    content.style.overflow = "visible"
    content.style.textOverflow = "clip"
  }
}

const t = useI18n()
const organizationSwitcherService = useService(OrganizationSwitcherService)

const currentUser = useReadonlyStream(
  platform.auth.getCurrentUserStream(),
  platform.auth.getCurrentUser()
)

const probableUser = useReadonlyStream(
  platform.auth.getProbableUserStream(),
  platform.auth.getProbableUser()
)

const isSignedIn = computed(() => currentUser.value !== null)
const userEmail = computed(() => probableUser.value?.email ?? null)

const rootDomain = computed(() => {
  return platform.organization?.getRootDomain?.() ?? "hoppscotch.io"
})

const isLoading = computed(() => organizationSwitcherService.isLoading.value)
const isOnDefaultInstance = organizationSwitcherService.isOnDefaultInstance
const error = computed(() => organizationSwitcherService.error.value)

const organizationItems = computed(
  () => organizationSwitcherService.switcherItems.value
)

const activeOrganizations = computed(() => {
  return organizationItems.value.filter(
    (item) => item.organization.orgStatus === OrganizationStatus.Active
  )
})

const inactiveOrganizations = computed(() => {
  return organizationItems.value.filter(
    (item) => item.organization.orgStatus !== OrganizationStatus.Active
  )
})

const getAdminUrl = (orgDomain: string) => {
  return `${window.location.protocol}//${orgDomain}.${rootDomain.value}/admin`
}

const handleSwitchToHoppscotch = () => {
  organizationSwitcherService.switchToOrganization(null)
  emit("close-dropdown")
}

const handleSwitchToOrganization = (orgDomain: string) => {
  organizationSwitcherService.switchToOrganization(orgDomain)
  emit("close-dropdown")
}

const retryFetch = () => {
  organizationSwitcherService.fetchMyOrganizations()
}

const handleInitiateOnboarding = () => {
  if (!isSignedIn.value) {
    emit("close-dropdown")
    invokeAction("modals.login.toggle")
    return
  }

  if (platform.organization?.initiateOnboarding) {
    platform.organization.initiateOnboarding()
  }
}
</script>

<style lang="scss" scoped>
.org-item {
  @apply relative;
  @apply flex items-center;
  @apply w-full;
  @apply px-4 py-2.5;
  @apply text-left;
  @apply hover:bg-primaryDark;
  @apply focus:outline-none focus-visible:bg-primaryDark;
  @apply transition-colors;
  @apply border-b border-dividerLight;

  &.active {
    @apply bg-primaryDark;
  }

  &.org-item-inactive {
    @apply opacity-60;
    @apply hover:opacity-80;
  }

  &.org-item-cta {
    @apply hover:bg-primaryLight;
    @apply border-b-0;
  }
}

.org-avatar {
  @apply flex items-center justify-center;
  @apply h-8 w-8;
  @apply rounded-md;
  @apply text-xs font-semibold;
  @apply shrink-0;
}

.org-info {
  @apply flex flex-col;
  @apply ml-3;
  @apply flex-1;
  @apply min-w-0;
}

.org-name {
  @apply text-sm font-medium;
  @apply text-secondaryDark;
  @apply truncate;
}

.org-domain {
  @apply text-xs;
  @apply text-secondary;
  @apply truncate;
}

.org-badge {
  @apply text-xs;
  @apply text-accent;

  &.org-badge-link {
    @apply inline-flex items-center gap-0.5;
    @apply px-1.5 py-0.5;
    @apply rounded;
    @apply hover:bg-accent hover:text-accentContrast;
    @apply transition-colors;
  }
}
</style>
